import { AnnotationMode, getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { PDFViewer, EventBus, PDFLinkService } from 'pdfjs-dist/legacy/web/pdf_viewer.mjs';
import pdfjsCssUrl from 'pdfjs-dist/legacy/web/pdf_viewer.css?url';
import { captureException } from '@sentry/vue';
import { onMounted, toValue, watch } from 'vue';
import { MaybeRefOrGetter } from '@vueuse/core';

import { getCrossOriginWorkerUri, ScreenSizes } from '~~core';

import PDFJSWorkerUrl from '../../workers/pdfjs-wrapper.worker?worker&url';
import { PDF_SCALES } from '../../constants/pdf';

export type UsePDFJsOptions = {
  initialScale?: string,
  onPageChange?: (ev: { pageNumber: number }) => void;
  onPageInit?: () => void;
  onDocumentLoad?: () => void;
  onScaleChange?: () => void;
  onPagesLoad?: (pagesCount: number) => void;
  limitPdfToScale1?: Ref<boolean>;
};

let pdfWorkerInstance: Worker | null = null;

const PDF_CONFIG = {
  scale: PDF_SCALES.AUTO,
  minZoomThreshold: 10,
};

function getScale1OrPageWidth() {
  const isMobile = window.innerWidth < ScreenSizes.MEDIUM;

  if (isMobile) return PDF_SCALES.PAGE_WIDTH;

  // 0.75 is the threshold to make scale be 1 after pdf.js calculations
  return '0.75';
}

export function usePDFJs(
  documentUrl: MaybeRefOrGetter<string>,
  container: MaybeRefOrGetter<HTMLDivElement>,
  options: UsePDFJsOptions = {},
) {
  const {
    initialScale = PDF_CONFIG.scale,
    onPageChange,
    onPageInit,
    onDocumentLoad,
    onScaleChange,
    onPagesLoad,
    limitPdfToScale1,
  } = options;

  useHead({
    link: [{
      rel: 'stylesheet',
      href: pdfjsCssUrl,
    }],
  });

  const eventBus = new EventBus();
  let pdfViewer: PDFViewer;
  const crossOriginWorkerUrl = getCrossOriginWorkerUri(PDFJSWorkerUrl);

  pdfWorkerInstance ??= new Worker(crossOriginWorkerUrl, { type: import.meta.env.DEV ? 'module' : 'classic' });
  GlobalWorkerOptions.workerPort = pdfWorkerInstance;

  function initPdfViewer() {
    const pdfLinkService = new PDFLinkService({ eventBus });

    /**
     * references: @link https://github.com/mozilla/pdf.js/blob/v2.14.305/web/base_viewer.js#L83
     */
    pdfViewer = new PDFViewer({
      container: toValue(container),
      annotationMode: AnnotationMode.ENABLE_FORMS,
      linkService: pdfLinkService,
      textLayerMode: 0,
      eventBus,
    });

    pdfLinkService.setViewer(pdfViewer);
  }

  function resetZoom() {
    pdfViewer.currentScaleValue = limitPdfToScale1?.value ? getScale1OrPageWidth() : initialScale;
  }

  function increaseZoom() {
    pdfViewer.increaseScale();
  }

  function decreaseZoom() {
    pdfViewer.decreaseScale();
  }

  function handlePagesLoaded(ev: { pagesCount: number; }) {
    resetZoom();
    onPagesLoad?.(ev.pagesCount);
  }

  async function loadPdf() {
    const pdfUrl = toValue(documentUrl);
    const loadingTask = getDocument({ url: pdfUrl, isEvalSupported: false });

    try {
      const pdfDocument = await loadingTask.promise;

      pdfViewer.setDocument(pdfDocument);
    } catch (error) {
      captureException(error, (scope) => {
        scope.setTag('pdfjs', true);
        scope.setContext('pdfjs', { pdfUrl });

        return scope;
      });

      throw error;
    }

    onDocumentLoad?.();
  }

  // Defined in https://github.com/mozilla/pdf.js/blob/v2.14.305/web/base_viewer.js#L798
  eventBus.on('pagesloaded', handlePagesLoaded);

  // Defined in https://github.com/mozilla/pdf.js/blob/v2.14.305/web/base_viewer.js#L411
  if (onPageInit) eventBus.on('pagesinit', onPageInit);
  if (onPageChange) eventBus.on('pagechanging', onPageChange);
  if (onScaleChange) eventBus.on('scalechanging', onScaleChange);

  onMounted(async () => {
    initPdfViewer();
    await loadPdf();
  });

  onUnmounted(() => {
    eventBus.off('pagesloaded', handlePagesLoaded);

    // Defined in https://github.com/mozilla/pdf.js/blob/v2.14.305/web/base_viewer.js#L411
    if (onPageInit) eventBus.off('pagesinit', onPageInit);
    if (onPageChange) eventBus.off('pagechanging', onPageChange);
    if (onScaleChange) eventBus.off('scalechanging', onScaleChange);
  });

  watch(() => toValue(documentUrl), () => loadPdf());

  return {
    resetZoom,
    increaseZoom,
    decreaseZoom,
  };
}
